import { Component, computed, inject, OnDestroy } from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import { UserStateService } from '../../../../core/services/authentication/user-state.service';

import { NotificationsService } from 'src/app/core/api/services/notifications.service';
import { OrganizationsService } from 'src/app/core/api/services/organizations.service';
import { InfiniteScrollDirective } from '../../../../core/directives/infinite-scroll.directive';
import {
  TabGroupComponent,
  TabItem,
} from '../../tab-group-component/tab-group-component.component';
import { AppNotificationService } from './../../../../core/services/app-notification.service';
import { TranslationModule } from './../../../../i18n/translation.module';
import { NotificationItemComponent } from './notification-item/notification-item.component';

@Component({
  selector: 'notifications',
  imports: [
    TranslationModule,
    NotificationItemComponent,
    InfiniteScrollDirective,
    TabGroupComponent,
  ],
  templateUrl: './notifications.component.html',
})
export class NotificationsComponent implements OnDestroy {
  //#region properties
  activeTab: 'unreadMessages' | 'allMessages' = 'unreadMessages';

  appNotificationService = inject(AppNotificationService);
  notificationsService = inject(NotificationsService);
  organizationsService = inject(OrganizationsService);
  userSession = inject(UserStateService);

  subscription: Subscription;
  loading = false;
  reachedEnd = false;
  pageNumber = 2;
  pageSize = 30;
  totalCount = 0;

  //#endregion
  tabs = computed<TabItem[]>(() => [
    {
      id: 'unreadMessages',
      title: 'common.unreadMessages',
      badge: this.appNotificationService.unreadNotificationsCount(),
    },
    {
      id: 'allMessages',
      title: 'common.allMessages',
      badge: this.appNotificationService.allNotificationsCount(),
    },
  ]);
  //#region api

  getNotificationListApi() {
    // prevent duplicate calls
    if (this.loading || this.reachedEnd) return;

    this.loading = true;

    this.subscription = this.appNotificationService
      .getNotificationsApi(true, this.pageSize, this.pageNumber)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((res) => {
        if (!res?.isSucceed) return;
        this.handleSuccessResponse(res.data);
      });
  }

  notificationsSeenApi() {
    if (this.subscription) this.subscription.unsubscribe();
    this.loading = true;

    this.subscription = this.notificationsService
      .notificationsCreateNotificationsSeen({ body: {} })
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((res) => {
        if (res.isSucceed) this.refreshNotifications();
      });
  }

  refreshNotifications(): void {
    // reset pagination so the list (and counts) are fully refetched
    this.pageNumber = 1;
    this.reachedEnd = false;
    this.loading = false;
    this.getNotificationListApi();
  }

  //#endregion

  ngOnDestroy() {
    if (this.subscription) this.subscription.unsubscribe();
  }

  //#region infinite scroll

  onScrollDown(): void {
    if (this.loading || this.reachedEnd) return;
    this.getNotificationListApi();
  }

  handleSuccessResponse(data: any): void {
    const items = data?.items ?? [];

    this.totalCount = data?.totalCount ?? 0;

    // ✅ stop infinite scroll when no more data
    if (items.length === 0) {
      this.reachedEnd = true;
      return;
    }

    // ✅ increment page AFTER success
    this.pageNumber++;

    // ✅ detect end by count
    const currentLength =
      this.appNotificationService.unreadNotifications().length;

    if (currentLength >= this.totalCount) {
      this.reachedEnd = true;
    }
  }

  //#endregion
}
